// What went wrong with a decision or a merge, in the vocabulary the panel renders.
import { ApiError } from '@/services/api/client'

/**
 * A refused decision: the 409 of ADR-0039, the 403 of a member below
 * collaborator (PRD-0032 SC-036) — which trying again cannot help — or a plain
 * failure.
 */
export type DecisionErrorKey = 'conflict' | 'forbidden' | 'decisionError'

/**
 * A refused merge (ADR-0042): the 409 — the face is no longer another identity's
 * face suggested here, or the two identities share a photo — the 404 of an
 * identity that is gone, the same 403, or a plain failure.
 */
export type MergeErrorKey = 'mergeConflict' | 'mergeGone' | 'forbidden' | 'mergeError'

/** Every refusal a face surface can show, as the suffix of its i18n key. */
export type FaceActionErrorKey = DecisionErrorKey | MergeErrorKey

/** One action's refusal, stamped with when the action was submitted (0: never). */
interface Refusal<Key extends FaceActionErrorKey> {
  at: number
  key: Key | null
}

/** Only the last action speaks, so an older refusal never outlives a later success. */
export function latestRefusal<Key extends FaceActionErrorKey>(
  refusals: Refusal<Key>[]
): Key | null {
  return refusals.reduce((latest, refusal) => (refusal.at > latest.at ? refusal : latest)).key
}

const isStatus = (error: unknown, status: number) =>
  error instanceof ApiError && error.status === status

/** The i18n suffix for a failed decision: the 409 of ADR-0039, the 403 or a plain failure. */
export function decisionErrorKey(error: unknown): DecisionErrorKey | null {
  if (!error) return null
  if (isStatus(error, 403)) return 'forbidden'
  return isStatus(error, 409) ? 'conflict' : 'decisionError'
}

/**
 * The i18n suffix for a refused change to one of an identity's confirmed faces —
 * its reference toggle, the main photo — on the identity page's faces card. The
 * 404 says there what the 409 does: the face is no longer this identity's (it
 * was unconfirmed, or went with a re-extraction or a merge), so trying again
 * cannot help.
 */
export function confirmedFaceErrorKey(error: unknown): DecisionErrorKey | null {
  return isStatus(error, 404) ? 'conflict' : decisionErrorKey(error)
}

/** The i18n suffix for a failed merge (ADR-0042). */
export function mergeErrorKey(error: unknown): MergeErrorKey | null {
  if (!error) return null
  if (isStatus(error, 403)) return 'forbidden'
  if (isStatus(error, 409)) return 'mergeConflict'
  if (isStatus(error, 404)) return 'mergeGone'
  return 'mergeError'
}
